/* eslint-disable react-dom/no-missing-iframe-sandbox */
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import short from "short-uuid";
import { toast } from "sonner";

import {
  useCreateRecording,
  useUpdateRecordingMeta,
} from "@/api/mutations";
import {
  useGetMyProfile,
  useGetMyRecordingsDetailed,
  useGetMySong,
  useGetRecordingMetas,
} from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import FormError from "@/components/FormError";
import H2 from "@/components/H2";
import Input from "@/components/Input";
import LoadingSmall from "@/components/LoadingSmall";
import RebrandButtonLink from "@/components/RebrandButtonLink";
import RebrandPrimaryButton from "@/components/RebrandPrimaryButton";
import Spacer from "@/components/Spacer";
import { useSpotifyOperations } from "@/features/songs/hooks/useSpotifyOperations";
import useGetColours from "@/helpers/getColours";
import { sanitizeInput, sendEvent, sendProperty } from "@/helpers/helpers";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";
import { useUIStateStore } from "@/store/uiStateStore";

// const short = require('short-uuid');

function RecordingForm(props) {
  const { id, closeForm, aac = null } = props;
  const flags = useFlags();
  // const accountFromState = AppState.useState(s => s.selectedAccount);
  const accountFromState = useAppStore(s => s.selectedAccount);
  const selectedAccount = aac || accountFromState;
  const song = useGetMySong({ aac: selectedAccount, wc: id });
  const recordings = useGetMyRecordingsDetailed({ aac: selectedAccount, wc: id });
  const { data: siteConfig } = useSiteConfigAuth();
  const { highlightColour } = useGetColours(siteConfig);
  const isTunecore = useUIStateStore(state => state.isTunecore);

  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [errors, setErrors] = useState({});

  const [title, setTitle] = useState();
  const [performingartist, setPerformingartist] = useState();
  const [isrc, setIsrc] = useState("");
  const [recordlabel, setRecordlabel] = useState();
  const [releasedate, setReleasedate] = useState();

  const [spotifyResult, setSpotifyResult] = useState();// Add these state variables
  const [spotifyURL, setSpotifyURL] = useState("");
  const [spotifyLinkErrorMessage, setSpotifyLinkErrorMessage] = useState("");

  const [recordingId, setRecordingId] = useState(null);

  const recordingMetas = useGetRecordingMetas({ id: recordingId });

  const createMutation = useCreateRecording();
  const recordingMetaMutation = useUpdateRecordingMeta();

  const profile = useGetMyProfile();

  const { getSpotifyTrackByUrl, isLoading: spotifyLoading } = useSpotifyOperations();

  const { t } = useTranslation("songs");
  const isrcRegex = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/;

  useEffect(() => {
    if (selectedAccount) {
      song.refetch();
    }
  }, [selectedAccount, song]);

  if (song.isError || song.data === null) {
    closeForm();
  }

  const checkErrors = (passed = {}) => {
    const errs = {};
    let valid = true;
    // title check
    const validTitle = title?.trim().length > 0;
    if (!validTitle) {
      errs.title = t("regex:recording-enter-title-regex");
      valid = false;
    }

    // PA check
    const validPA = performingartist?.trim().length > 0;
    if (!validPA) {
      errs.performingartist = t("regex:recording-add-performing-artist-regex");
      valid = false;
    }

    const isrcPassed = passed.isrc || isrc;
    const enteredIsrc = isrcPassed?.trim().length > 0;

    if (!isrcRegex.test(isrcPassed) && enteredIsrc) {
      valid = false;
      errs.isrc = t("regex:recording-incorrect-isrc");
    }

    setErrors(errs);
    return valid;
  };

  const addRecordingMeta = (rid) => {
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    recordingMetaMutation.reset();
    setRecordingId(rid);
    const data = {
      id: rid,
      spotify_id: spotifyResult ? spotifyResult.id : null,
      thumb_url: spotifyResult ? spotifyResult?.album?.images[0].url : null,
      song_id: song.data.workcode,
    };

    recordingMetaMutation.mutate(data, {
      onSuccess: (response) => {
        if (response.failedvalidation) {
          setValidationErrorMessage(response.data.messages);
          setPending(false);
        }
        else {
          song.refetch();
          recordings.refetch();
          recordingMetas.refetch();

          toast.success(`${t("songs:recording-added-toast")}: ${title}`);

          sendProperty({
            property: "last_recording_added",
            value: "CURRENT_DATE",
            email: profile?.data?.email,
          });
          sendEvent({ property: "pe14489857_recording_added" });

          closeForm();
          setPending(false);
        }
      },
      onError: (error) => {
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  const addRecording = async () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    // reset mutation
    createMutation.reset();
    const valid = checkErrors();
    const date = releasedate?.length === 4 ? `${releasedate}-01-01` : releasedate;
    if (valid) {
      const data = {
        artistaccountcode: selectedAccount,
        title: sanitizeInput(title),
        performingartist: sanitizeInput(performingartist),
        isrc,
        recordlabel,
        // generate out own custom code as UIC not actually used
        // so we can use if for matching front-end data with backend data
        uicbarcode: short.generate().substring(0, 13),
        releasedate: date,
        workcode: song.data.workcode,
        spotifyidentifier: spotifyResult?.id,
        spotifypopularity: spotifyResult?.popularity,
        spotifypopularitylastupdatedutc: spotifyResult?.id === undefined || spotifyResult?.id === null
          ? null
          : new Date().toISOString(),
        etag: song.data.etag,
      };
      createMutation.mutate(data, {
        onSuccess: (result) => {
          if (result.failedvalidation) {
            setValidationErrorMessage(result.messages.join(", "));
            setPending(false);
          }
          else {
            addRecordingMeta(result.model.uicbarcode, null);
          }
        },
        onError: (error) => {
          setMutationErrorMessage(error.message);
          setPending(false);
        },
      });
    }
    else {
      setPending(false);
    }
  };

  const handleChangeSpotifyResult = (track) => {
    const date = track.album.release_date?.length === 4
      ? `${track.album.release_date}-01-01`
      : track.album.release_date;
    setTitle(sanitizeInput(track.name));
    setPerformingartist(sanitizeInput(track.artists[0].name));
    setIsrc(track?.external_ids?.isrc ?? "");
    setReleasedate(date);
    setSpotifyResult(track);
    setSpotifyLinkErrorMessage("");
  };

  const handleSpotifyURLSubmit = async (e) => {
    e.preventDefault();
    setPending(true);
    setSpotifyLinkErrorMessage("");
    setSpotifyResult(null);

    const track = await getSpotifyTrackByUrl(spotifyURL);

    if (track) {
      handleChangeSpotifyResult(track);
    }
    else {
      setSpotifyLinkErrorMessage(
        t("songs:link-spotify-track-invalid-url-error"),
      );
      setSpotifyResult(null);
    }
    setPending(false);
  };

  const removeSpotify = () => {
    setTitle("");
    setPerformingartist("");
    setIsrc("");
    setReleasedate(undefined);
    setSpotifyResult(null);
    setSpotifyURL("");
    setSpotifyLinkErrorMessage("");
  };

  if (song.isSuccess && song.data) {
    return (
      <div className={`${isTunecore ? "text-white" : ""}`}>
        <div className="mr-10">
          <H2>
            {t("songs:add-recording-header")}
            {" "}
            <b>{song.data.title}</b>
          </H2>
        </div>
        <Spacer thin />
        <div className="w-full">
          <div className="flex flex-row items-start justify-center w-full">
            {flags["pd-spotify-recording-song-details-feature"] && (
              <div
                style={{
                  backgroundColor: isTunecore ? "#282828" : highlightColour,
                }}
                className={
                  isTunecore
                    ? "text-white px-12 py-10 rounded-lg "
                    : "px-12 py-10 rounded-lg  text-old-sentric-dark-gray"
                }
              >
                <div className="flex flex-col">
                  {!spotifyResult && (
                    <>
                      <h2 className="text-xl">
                        {t("songs:import-from-spotify-header")}
                      </h2>
                      <Spacer thin />
                      <p className="font-sentric-alt">
                        {t("songs:import-from-spotify-text")}
                        {" "}
                        <span>
                          <a
                            className="underline hover:scale-105"
                            href="https://support.spotify.com/us/artists/article/finding-your-artist-url/"
                          >
                            {t("songs:link-spotify-instructions-link")}
                          </a>
                        </span>
                      </p>
                      <Spacer />
                      <Input
                        blackLabel
                        type="text"
                        placeholder={t("songs:link-spotify-paste-track-url")}
                        value={spotifyURL}
                        onChange={e => setSpotifyURL(e.target.value)}
                        error={spotifyLinkErrorMessage}
                        className="w-full mb-4 form-input"
                      />
                      <div className="self-end">
                        <RebrandPrimaryButton
                          type="submit"
                          onClick={handleSpotifyURLSubmit}
                          arrow
                          disabled={spotifyLoading || pending}
                          pending={spotifyLoading || pending}
                        >
                          {t("songs:link-spotify-fetch-track")}
                        </RebrandPrimaryButton>
                      </div>
                    </>
                  )}
                  {!!spotifyResult && (
                    <>
                      <div>
                        <iframe
                          title="preview"
                          src={`https://open.spotify.com/embed/track/${spotifyResult.id}`}
                          className="w-full lg:w-3/4"
                          height="80"
                          allow="encrypted-media"
                        />
                      </div>
                      <Spacer thin />
                      <div>
                        <button
                          type="button"
                          id="btnRecordingFormRemoveSelection"
                          className={`${
                            isTunecore
                              ? "text-white hover:text-tunecore-green"
                              : "text-black font-sentric-alt hover:scale-105"
                          } underline flex items-center`}
                          onClick={removeSpotify}
                        >
                          <span>{t("songs:remove-selection")}</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

          </div>
          <div className="">
            <Spacer />
            <h2 className="text-xl font-semibold">
              {t("songs:recording-details-subheader")}
            </h2>

            <Spacer thin />

            <Input
              required
              type="text"
              error={errors.title}
              placeholder={t("songs:title-placeholder")}
              value={title}
              onBlur={() => {}}
              onChange={(e) => {
                setTitle(sanitizeInput(e.target.value));
              }}
            />
            <Spacer thin />

            <Input
              required
              type="text"
              error={errors.performingartist}
              placeholder={t("songs:performed-by")}
              value={performingartist}
              onBlur={() => {}}
              onChange={(e) => {
                setPerformingartist(e.target.value);
              }}
            />

            <Spacer thin />

            <Input
              type="text"
              error={errors.isrc}
              placeholder={t("songs:isrc-placeholder")}
              value={isrc}
              onBlur={() => {}}
              onChange={(e) => {
                setIsrc(e.target.value);
              }}
            />
            <Spacer thin />

            <Input
              type="text"
              error={errors.recordlabel}
              placeholder={t("songs:record-label-placeholder")}
              value={recordlabel}
              onBlur={() => {}}
              onChange={(e) => {
                setRecordlabel(e.target.value);
              }}
            />

            <Spacer thin />

            <Input
              type="date"
              className={`${
                isTunecore ? "tunecore-datepicker" : "themed-datepicker"
              } relative z-20`}
              error={errors.releasedate}
              dateFormatCalendar=" "
              showMonthDropdown
              showYearDropdown
              dateDropdownMode="select"
              useShortMonthInDropdown
              placeholder={t("songs:release-date")}
              value={releasedate}
              onBlur={() => {}}
              onChange={(date) => {
                setReleasedate(date);
              }}
            />
          </div>
          <Spacer />
          <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
          <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
            <>
              <RebrandPrimaryButton
                id="btnRecordingFormAddRecording"
                onClick={addRecording}
                disabled={pending}
                pending={pending}
                arrow={!isTunecore}
              >
                {t("songs:add-recording-button")}
              </RebrandPrimaryButton>
              <RebrandButtonLink onClick={() => closeForm()}>
                {t("songs:cancel-and-close")}
              </RebrandButtonLink>
            </>
          </div>
        </div>
      </div>
    );
  }
  return <LoadingSmall />;
}
export default RecordingForm;
